import { useCallback, useEffect, useRef, useState } from 'react'

export type ScannerStatus = 'idle' | 'starting' | 'running' | 'denied' | 'unavailable' | 'error'

export interface BarcodeScanner {
  status: ScannerStatus
  /** Persian, ready to render. Null while nothing has gone wrong. */
  error: string | null
  videoRef: React.RefObject<HTMLVideoElement | null>
  /** The live track, so useTorch can drive the lamp on the same stream. */
  track: MediaStreamTrack | null
  retry: () => void
}

interface Options {
  /** False tears the camera down — a screen the operator navigated away from must not hold it. */
  enabled: boolean
  onDetect: (value: string) => void
  /**
   * Fraction of the frame that is actually decoded, centred. Cropping is the single
   * biggest win for decode rate on a handheld: it cuts the work per frame and stops the
   * reader locking onto a neighbouring label that happens to be in view.
   */
  region?: { width: number; height: number }
}

/** Same value twice inside this window is one label held in front of the lens, not two scans. */
const SAME_CODE_MS = 1500

/** Hard floor between any two accepted scans, so a scan storm cannot outrun the UI. */
const ANY_CODE_MS = 350

const DEFAULT_REGION = { width: 0.8, height: 0.45 }

interface DetectorLike {
  detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>
}

/**
 * Continuous barcode capture: BarcodeDetector where the platform has it, @zxing/browser
 * where it does not. Chrome on Android has BarcodeDetector, which is the target device;
 * zxing is the desktop and iOS fallback and is loaded on demand so it stays out of the
 * main bundle.
 */
export function useBarcodeScanner({ enabled, onDetect, region }: Options): BarcodeScanner {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [liveStatus, setLiveStatus] = useState<ScannerStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [track, setTrack] = useState<MediaStreamTrack | null>(null)
  const [attempt, setAttempt] = useState(0)

  // Held in refs so the scan loop never restarts when a callback identity changes, and
  // written in an effect rather than during render.
  const onDetectRef = useRef(onDetect)
  const regionRef = useRef(region)

  useEffect(() => {
    onDetectRef.current = onDetect
    regionRef.current = region
  }, [onDetect, region])

  const retry = useCallback(() => {
    setError(null)
    setAttempt((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!enabled) {
      return
    }

    const video = videoRef.current

    if (!video) {
      return
    }

    let cancelled = false
    let stream: MediaStream | null = null
    let frame = 0
    let lastValue = ''
    let lastValueAt = 0
    let lastAcceptedAt = 0

    const emit = (value: string) => {
      const trimmed = value.trim()
      const now = Date.now()

      if (trimmed === '' || now - lastAcceptedAt < ANY_CODE_MS) {
        return
      }

      if (trimmed === lastValue && now - lastValueAt < SAME_CODE_MS) {
        // Refresh the timestamp: a label left in front of the lens must not start
        // re-firing the moment the window lapses.
        lastValueAt = now
        return
      }

      lastValue = trimmed
      lastValueAt = now
      lastAcceptedAt = now
      onDetectRef.current(trimmed)
    }

    const fail = (status: ScannerStatus, message: string) => {
      if (!cancelled) {
        setLiveStatus(status)
        setError(message)
      }
    }

    const start = async () => {
      setLiveStatus('starting')

      // getUserMedia only exists in a secure context. Served over plain http from a
      // hostname like wms.local the whole API is simply absent, and without this the
      // screen would sit on an empty viewfinder with no explanation.
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        fail('unavailable', 'دوربین فقط در اتصال امن (HTTPS) کار می‌کند. کد را دستی وارد کنید.')
        return
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        })
      } catch (cause) {
        const denied = cause instanceof DOMException && cause.name === 'NotAllowedError'

        fail(
          denied ? 'denied' : 'error',
          denied
            ? 'دسترسی به دوربین رد شد. از تنظیمات مرورگر اجازه دهید یا کد را دستی وارد کنید.'
            : 'دوربین در دسترس نیست. کد را دستی وارد کنید.',
        )
        return
      }

      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      video.srcObject = stream
      await video.play().catch(() => undefined)

      setTrack(stream.getVideoTracks()[0] ?? null)
      setLiveStatus('running')

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d', { willReadFrequently: true })
      const detect = await createDetector()

      if (!detect || !context) {
        fail('unavailable', 'خواندن بارکد روی این دستگاه پشتیبانی نمی‌شود. کد را دستی وارد کنید.')
        return
      }

      const scan = async () => {
        if (cancelled) {
          return
        }

        if (video.readyState >= video.HAVE_CURRENT_DATA && video.videoWidth > 0) {
          const fraction = regionRef.current ?? DEFAULT_REGION
          const cropWidth = Math.round(video.videoWidth * fraction.width)
          const cropHeight = Math.round(video.videoHeight * fraction.height)

          canvas.width = cropWidth
          canvas.height = cropHeight
          context.drawImage(
            video,
            Math.round((video.videoWidth - cropWidth) / 2),
            Math.round((video.videoHeight - cropHeight) / 2),
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight,
          )

          const value = await detect(canvas)

          if (value !== null) {
            emit(value)
          }
        }

        frame = requestAnimationFrame(() => void scan())
      }

      frame = requestAnimationFrame(() => void scan())
    }

    void start()

    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
      stream?.getTracks().forEach((t) => t.stop())
      setTrack(null)
      // `video` is the element captured when the effect ran, not a ref read at teardown.
      video.srcObject = null
    }
  }, [enabled, attempt])

  // Derived rather than written from the effect: a disabled scanner is idle by definition.
  return { status: enabled ? liveStatus : 'idle', error, videoRef, track, retry }
}

type DetectFn = (canvas: HTMLCanvasElement) => Promise<string | null>

/**
 * One call shape over both engines, so the scan loop does not branch per frame.
 * Returns null when the platform can decode nothing at all.
 */
async function createDetector(): Promise<DetectFn | null> {
  const native = await createNativeDetector()

  if (native) {
    return async (canvas) => {
      try {
        const found = await native.detect(canvas)
        return found[0]?.rawValue ?? null
      } catch {
        // A single bad frame is normal; keep the loop alive.
        return null
      }
    }
  }

  try {
    const { BrowserMultiFormatReader } = await import('@zxing/browser')
    const reader = new BrowserMultiFormatReader()

    return (canvas) => {
      try {
        return Promise.resolve(reader.decodeFromCanvas(canvas).getText())
      } catch {
        // zxing throws NotFoundException on every frame without a barcode.
        return Promise.resolve(null)
      }
    }
  } catch {
    return null
  }
}

interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): DetectorLike
  getSupportedFormats?: () => Promise<string[]>
}

async function createNativeDetector(): Promise<DetectorLike | null> {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector

  if (!ctor) {
    return null
  }

  try {
    // Narrowing the format list is a real speed-up, but only formats the platform
    // actually supports may be passed — an unsupported one throws.
    const wanted = ['code_128', 'code_39', 'ean_13', 'qr_code', 'data_matrix']
    const supported = (await ctor.getSupportedFormats?.()) ?? []
    const formats = wanted.filter((format) => supported.includes(format))

    return new ctor(formats.length > 0 ? { formats } : undefined)
  } catch {
    return null
  }
}
